import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Label,
  Select,
} from '@theme-ui/components'
import { find, get, pipe } from 'lodash/fp'
import React, { createContext, memo, useContext, useState } from 'react'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { useMutation, useQueryClient } from 'react-query'
import { reportAction } from '../../../api/reportFunction'
import { Modal, Table } from '../../../components'
import { InputComponent } from '../../../components/Input/Input'
import { notifySuccess } from '../../../components/Notif'
import { newUuid } from '../../../utils'
import { ReportContext, Suspect } from './ReportForm'

interface AddSuspectModalProps {
  reportId: string | number
  currentSuspects: Array<Suspect>
}

const SuspectsContext = createContext({
  addedSuspects: [],
  setAddedSuspects: null,
  setSuspect: null,
})

export default function AddSuspectsModal({
  reportId,
  currentSuspects = [],
}: AddSuspectModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [addedSuspects, setAddedSuspects] = useState<Array<Suspect>>(
    currentSuspects.length
      ? currentSuspects.map((sus) => ({ id: newUuid(), ...sus }))
      : [{ id: newUuid(), namaPersonel: '', unitDivisi: '', proven: false }]
  )
  const client = useQueryClient()

  const { mutate, isLoading } = useMutation(
    (data: { suspects: Array<Omit<Suspect, 'id'>> }) =>
      reportAction(reportId, 'update-suspects', data),
    {
      onSuccess: (_) => {
        notifySuccess('Daftar pihak terlibat berhasil ditambah')
        setIsOpen(false)
        client.invalidateQueries('/reports/:id')
      },
    }
  )

  const toggle = () => setIsOpen(!isOpen)
  const addSuspect = () =>
    setAddedSuspects((prev) => [
      ...prev,
      { id: newUuid(), namaPersonel: '', unitDivisi: '', proven: false },
    ])

  const deleteSuspect = (id) =>
    setAddedSuspects((prev) => {
      return prev.filter((suspect) => suspect.id !== id)
    })

  function setSuspect(id, field, value) {
    setAddedSuspects((prev) => {
      return prev.map((suspect) => {
        if (id === suspect.id) return { ...suspect, [field]: value }
        return suspect
      })
    })
  }

  function handleSubmit() {
    mutate({ suspects: addedSuspects.map(({ id, ...sus }) => sus) })
  }

  return (
    <SuspectsContext.Provider
      value={{ addedSuspects, setAddedSuspects, setSuspect }}
    >
      <Button
        onClick={() => toggle()}
        sx={{ display: 'block', ml: 'auto', mr: 'auto', mb: 3 }}
      >
        <FaPlus /> Ubah Daftar
      </Button>

      <Modal
        isOpen={isOpen}
        onRequestClose={toggle}
        header="Tambahkan Nama terduga"
      >
        <Box sx={{ height: [100, 200, 350], overflowY: 'scroll' }}>
          <Table
            disablePagination
            options={{
              data: addedSuspects,
              columns: [
                {
                  Header: 'Nama Personel',
                  accessor: 'namaPersonel',
                  Cell: NameComponent,
                },
                {
                  Header: 'Unit Kerja',
                  accessor: 'unitDivisi',
                  Cell: UnitDivisionComponent,
                },
                {
                  Header: 'Terbukti',
                  accessor: 'proven',
                  width: '3%',
                  Cell: ProvenComponent,
                },
                {
                  Header: '',
                  accessor: 'action',
                  width: '4%',
                  Cell: (props) => {
                    const id = props.row.original.id
                    return (
                      <IconButton
                        title="Delete suspect"
                        onClick={(e) => {
                          deleteSuspect(id)
                        }}
                      >
                        <FaTrash />
                      </IconButton>
                    )
                  },
                },
              ],
            }}
            keyExtractor={(suspect) => suspect.id}
          />
        </Box>
        <Button
          // @todo: remove sx
          sx={{
            bg: 'transparent !important',
            border: 'unset',
            display: 'block',
            ml: 'auto',
            mr: 'auto',
            ':hover': { color: 'primary' },
          }}
          onClick={addSuspect}
          variant="outline"
        >
          Tambahkan
        </Button>

        <Button
          sx={{ display: 'block', ml: 'auto' }}
          disabled={isLoading}
          onClick={handleSubmit}
        >
          Selesai
        </Button>
      </Modal>
    </SuspectsContext.Provider>
  )
}

const NameComponent = memo((props: any) => {
  const id = props.row.original.id
  const { addedSuspects, setSuspect } = useContext(SuspectsContext)
  const [value, setValue] = useState(
    pipe(find({ id }), get('namaPersonel'))(addedSuspects)
  )

  return (
    <InputComponent
      key={id}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={(e) => setSuspect(id, 'namaPersonel', value)}
    />
  )
})

const UnitDivisionComponent = memo((props: any) => {
  const id = props.row.original.id
  const { addedSuspects, setSuspect } = useContext(SuspectsContext)
  const { listDivisi } = useContext(ReportContext)

  return (
    <Select
      required
      onChange={(e) => setSuspect(id, 'unitDivisi', e.target.value)}
      value={pipe(find({ id }), get('unitDivisi'))(addedSuspects)}
    >
      <option value=""></option>
      {listDivisi.map((division) => (
        <option key={division.code} value={division.code}>
          {division.value}
        </option>
      ))}
    </Select>
  )
})

const ProvenComponent = memo((props: any) => {
  const id = props.row.original.id
  const { addedSuspects, setSuspect } = useContext(SuspectsContext)
  const checked = pipe(find({ id }), get('proven'))(addedSuspects)

  return (
    <Label>
      <Checkbox
        checked={checked}
        onChange={(e) => setSuspect(id, 'proven', e.target.checked)}
      />
    </Label>
  )
})
